import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Flex,
  Input,
  Result,
  Segmented,
  Statistic,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { Download } from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  getListSuperadminFailedBillingEventsQueryKey,
  useGetSuperadminBillingReport,
  useListSuperadminBillingAdjustments,
  useListSuperadminFailedBillingEvents,
  useReplaySuperadminBillingEvent,
} from '../../../api';
import { apiErrorMessage, formatCredits } from '../../../modules/billing';
import {
  BillingHowItWorks,
  CreditsCost,
  HelpLabel,
  SuperadminBillingPlans,
  SuperadminOrganizationBillingDrawer,
} from '../../../modules/superadmin';
import { billingStatusLabel } from '../../../modules/superadmin/utils/superadminBillingDisplay';
import {
  ADJUSTMENTS_HELP,
  AUTO_RECHARGE_STATE_HELP,
  BLOCKED_CODE_HELP,
  CHURN_RISK_HELP,
  CREDITS_HELP,
  FAILED_EVENTS_HELP,
  GRANT_KIND_HELP,
  HEALTH_HELP,
  MIX_HELP,
  MONEY_HELP,
  ORGANIZATIONS_HELP,
  PERIOD_HELP,
} from '../../../modules/superadmin/utils/superadminBillingHelp';
import {
  AUTO_RECHARGE_STATE_LABELS,
  CHURN_RISK_LABELS,
  GRANT_KIND_LABELS,
  adjustmentActorLabel,
  blockedCodeLabel,
  creditsInDollars,
  formatCents,
  formatDollars,
  formatSignedCredits,
  supportReasonLabel,
  usageTypeLabel,
} from '../../../modules/superadmin/utils/superadminBillingSupport';
import { Loader } from '../../../ui/components/loader';
import { formatDate, formatDateTime } from '../../../ui/utils/format-date';

import type { GetSuperadminBillingReportResponseDtoOrganizationsItem } from '../../../api';

const { Text, Title } = Typography;

type OrgRow = GetSuperadminBillingReportResponseDtoOrganizationsItem;
type OrgFilter = 'all' | 'blocked' | 'churn' | 'debt';

const csvCell = (value: unknown): string => `"${String(value ?? '').replaceAll('"', '""')}"`;

const BillingReportRoute = () => {
  const queryClient = useQueryClient();
  const [range, setRange] = useState<[Dayjs, Dayjs]>(() => [dayjs().startOf('month'), dayjs()]);
  const [filter, setFilter] = useState<OrgFilter>('all');
  const [search, setSearch] = useState('');
  const [billingOrganizationId, setBillingOrganizationId] = useState<string | null>(null);
  const window = { from: range[0].startOf('day').toISOString(), to: range[1].endOf('day').toISOString() };
  const report = useGetSuperadminBillingReport(window);
  const failed = useListSuperadminFailedBillingEvents();
  const adjustments = useListSuperadminBillingAdjustments(window);
  const replay = useReplaySuperadminBillingEvent();

  const organizations = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const rows = (report.data?.data.organizations ?? []).filter((row) => !needle || row.name.toLowerCase().includes(needle));
    if (filter === 'blocked') return rows.filter((row) => row.blockedCode);
    if (filter === 'churn') return rows.filter((row) => row.churnRisk.length > 0);
    if (filter === 'debt') return rows.filter((row) => row.debt > 0);
    return rows;
  }, [report.data, filter, search]);

  const adjustmentTotals = useMemo(() => {
    const rows = adjustments.data?.data.items ?? [];
    const sum = (kind: 'added' | 'removed') =>
      rows.filter((row) => row.kind === kind).reduce((total, row) => total + row.credits, 0);
    return { added: sum('added'), removed: sum('removed') };
  }, [adjustments.data]);

  const replayEvent = async (stripeEventId: string) => {
    try {
      const result = await replay.mutateAsync({ stripeEventId });
      if (result.data.ok) message.success('Replayed: the event went through');
      else message.error(`Failed again: ${result.data.error}`);
      void queryClient.invalidateQueries({ queryKey: getListSuperadminFailedBillingEventsQueryKey() });
    } catch (error) {
      message.error(apiErrorMessage(error, 'Replay failed'));
    }
  };

  const exportAdjustments = () => {
    const rows = adjustments.data?.data.items ?? [];
    const pricing = report.data?.data.pricing;
    const header = ['When', 'Organization', 'Kind', 'Credits', 'Value ($)', 'Max cost to K3X ($)', 'Reason', 'Note', 'By'];
    const lines = rows.map((row) => {
      const dollars = pricing ? creditsInDollars(row.credits, pricing) : null;
      return [
        row.createdAt,
        row.organizationName,
        row.kind,
        row.credits,
        dollars?.retailDollars.toFixed(2),
        dollars?.maxCostDollars.toFixed(2),
        supportReasonLabel(row.reasonCode),
        row.note,
        adjustmentActorLabel(row),
      ]
        .map(csvCell)
        .join(',');
    });
    const blob = new Blob([[header.map(csvCell).join(','), ...lines].join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `k3x-credit-adjustments-${range[0].format('YYYY-MM-DD')}-${range[1].format('YYYY-MM-DD')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (report.isPending) return <Loader />;
  if (report.isError || !report.data) {
    return <Result status="error" title="Failed to load the billing report" extra={<Button onClick={() => void report.refetch()}>Retry</Button>} />;
  }
  const { money, credits, health, mix, pricing } = report.data.data;
  const period = <Text type="secondary">{`${formatDate(range[0].toISOString())} – ${formatDate(range[1].toISOString())}`}</Text>;
  const givenBySupport = credits.granted.find((row) => row.kind === 'adjustment')?.credits ?? 0;
  const addedDollars = creditsInDollars(adjustmentTotals.added, pricing);

  return (
    <Flex vertical gap={16} style={{ padding: 24 }} data-testid="superadmin-billing-report">
      <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
        <Flex align="center" gap={8}>
          <Title level={3} style={{ margin: 0 }}>
            Billing
          </Title>
          <BillingHowItWorks pricing={pricing} />
        </Flex>
        <Flex align="center" gap={8}>
          <HelpLabel label={<Text type="secondary">Period</Text>} help={PERIOD_HELP.range} />
          <DatePicker.RangePicker
            value={range}
            allowClear={false}
            onChange={(value) => {
              if (value?.[0] && value[1]) setRange([value[0], value[1]]);
            }}
          />
        </Flex>
      </Flex>

      <Tabs
        items={[
          {
            key: 'overview',
            label: 'Overview',
            children: (
              <Flex vertical gap={16}>
                <Card size="small" title="Money" extra={period}>
                  <Flex gap={32} wrap="wrap">
                    <Statistic title={<HelpLabel label="MRR" help={MONEY_HELP.mrr} />} value={formatCents(money.mrrCents)} />
                    <Statistic
                      title={<HelpLabel label="Collected" help={MONEY_HELP.collected} />}
                      value={money.revenueCents.available ? formatCents(money.revenueCents.total) : '—'}
                    />
                    <Statistic title={<HelpLabel label="Plans" help={MONEY_HELP.plans} />} value={formatCents(money.revenueCents.plan)} />
                    <Statistic title={<HelpLabel label="Top-ups" help={MONEY_HELP.topUps} />} value={formatCents(money.revenueCents.topUps)} />
                    <Statistic
                      title={<HelpLabel label="Auto-recharge" help={MONEY_HELP.autoRecharge} />}
                      value={formatCents(money.revenueCents.autoRecharge)}
                    />
                    <Statistic
                      title={<HelpLabel label="Other (numbers, A2P)" help={MONEY_HELP.other} />}
                      value={formatCents(money.revenueCents.other)}
                    />
                    <Statistic
                      title={<HelpLabel label="Refunds" help={MONEY_HELP.refunds} />}
                      value={`${money.refunds.count} · ${formatCents(money.refunds.cents)}`}
                    />
                    <Statistic title={<HelpLabel label="Open disputes" help={MONEY_HELP.disputes} />} value={money.disputesOpen} />
                  </Flex>
                  {!money.revenueCents.available && (
                    <Alert type="warning" showIcon title="Stripe could not be read: collected money is not shown" style={{ marginTop: 12 }} />
                  )}
                </Card>
                <Card size="small" title="Credits" extra={period}>
                  <Flex gap={32} wrap="wrap">
                    <Statistic
                      title={<HelpLabel label="Consumed" help={CREDITS_HELP.consumed} />}
                      value={formatCredits(credits.consumed)}
                      data-testid="report-consumed"
                    />
                    <Statistic
                      title={<HelpLabel label="Retail value" help={CREDITS_HELP.retailValue} />}
                      value={`$${credits.retailValueDollars.toFixed(2)}`}
                    />
                    <Statistic
                      title={<HelpLabel label="Provider cost" help={CREDITS_HELP.providerCost} />}
                      value={`$${credits.providerCostDollars.toFixed(2)}`}
                    />
                    <Statistic
                      title={<HelpLabel label="Gross margin" help={CREDITS_HELP.grossMargin(pricing.costMarkup)} />}
                      value={credits.grossMarginPct === null ? '—' : `${credits.grossMarginPct}%`}
                    />
                    <Statistic
                      title={<HelpLabel label="Expired unused" help={CREDITS_HELP.expiredUnused} />}
                      value={formatCredits(credits.expiredUnused)}
                    />
                    <Flex vertical>
                      <Statistic
                        title={<HelpLabel label="Given by support" help={CREDITS_HELP.givenBySupport} />}
                        value={formatCredits(givenBySupport)}
                        data-testid="report-given-by-support"
                      />
                      <CreditsCost credits={givenBySupport} pricing={pricing} compact />
                    </Flex>
                    <Statistic
                      title={<HelpLabel label="Taken back by support" help={CREDITS_HELP.takenBack} />}
                      value={formatCredits(credits.takenBackBySupport)}
                    />
                  </Flex>
                  <Flex gap={8} wrap="wrap" align="center" style={{ marginTop: 12 }}>
                    {credits.granted.length > 0 && <HelpLabel label={<Text type="secondary">Granted</Text>} help={CREDITS_HELP.granted} />}
                    {credits.granted.map((row) => (
                      <Tooltip key={row.kind} title={GRANT_KIND_HELP[row.kind]}>
                        <Tag>
                          {GRANT_KIND_LABELS[row.kind] ?? row.kind}: {formatCredits(row.credits)} ({row.grants})
                        </Tag>
                      </Tooltip>
                    ))}
                  </Flex>
                </Card>
                <Card
                  size="small"
                  title={<HelpLabel label="Health" help={PERIOD_HELP.health} />}
                  extra={<Text type="secondary">Now</Text>}
                >
                  <Flex gap={32} wrap="wrap">
                    <Statistic title={<HelpLabel label="Active" help={HEALTH_HELP.active} />} value={health.active} />
                    <Statistic title={<HelpLabel label="Trialing" help={HEALTH_HELP.trialing} />} value={health.trialing} />
                    <Statistic title={<HelpLabel label="Past due" help={HEALTH_HELP.pastDue} />} value={health.pastDue} />
                    <Statistic title={<HelpLabel label="Ended" help={HEALTH_HELP.ended} />} value={health.ended} />
                    <Statistic title={<HelpLabel label="Low (< 10,000)" help={HEALTH_HELP.low('10,000')} />} value={health.low} />
                    <Statistic title={<HelpLabel label="Out of credits" help={HEALTH_HELP.exhausted} />} value={health.exhausted} />
                    <Statistic
                      title={<HelpLabel label="In debt" help={HEALTH_HELP.inDebt} />}
                      value={`${health.inDebt} · ${formatCredits(health.totalDebt)}`}
                    />
                    <Statistic
                      title={<HelpLabel label="Auto-recharge charges" help={HEALTH_HELP.autoRechargeCharges} />}
                      value={health.autoRechargeCharges}
                    />
                    <Statistic
                      title={<HelpLabel label="Auto-recharge declines" help={HEALTH_HELP.autoRechargeFailures} />}
                      value={health.autoRechargeFailures}
                    />
                    <Statistic
                      title={<HelpLabel label="Failed Stripe events" help={HEALTH_HELP.failedStripeEvents} />}
                      value={health.failedStripeEvents}
                    />
                    <Statistic title={<HelpLabel label="Churn risk" help={HEALTH_HELP.churnRisk} />} value={health.churnRisk} />
                  </Flex>
                </Card>
                <Card size="small" title={<HelpLabel label="What the credits were spent on" help={MIX_HELP.title} />} extra={period}>
                  <Table
                    size="small"
                    rowKey="usageType"
                    pagination={false}
                    dataSource={mix}
                    locale={{ emptyText: 'Nothing spent in this window' }}
                    columns={[
                      { title: 'Action', render: (_, row) => usageTypeLabel(row.usageType) },
                      { title: 'Credits', align: 'right', render: (_, row) => formatCredits(Math.ceil(row.credits)) },
                      {
                        title: <HelpLabel label="Share" help={MIX_HELP.share} />,
                        align: 'right',
                        render: (_, row) => (credits.consumed > 0 ? `${Math.round((row.credits / credits.consumed) * 100)}%` : '—'),
                      },
                      {
                        title: <HelpLabel label="Provider cost" help={MIX_HELP.providerCost} />,
                        align: 'right',
                        render: (_, row) => `$${row.providerCost.toFixed(2)}`,
                      },
                    ]}
                  />
                </Card>
              </Flex>
            ),
          },
          {
            key: 'organizations',
            label: 'Organizations',
            children: (
              <Flex vertical gap={12}>
                <Text type="secondary">{ORGANIZATIONS_HELP.intro}</Text>
                <Flex gap={12} wrap="wrap">
                <Input.Search
                  allowClear
                  placeholder="Search organizations"
                  style={{ maxWidth: 320 }}
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  data-testid="report-org-search"
                />
                <Segmented<OrgFilter>
                  value={filter}
                  onChange={setFilter}
                  options={[
                    { value: 'all', label: <Tooltip title={ORGANIZATIONS_HELP.filters.all}><span>All</span></Tooltip> },
                    { value: 'blocked', label: <Tooltip title={ORGANIZATIONS_HELP.filters.blocked}><span>Blocked</span></Tooltip> },
                    { value: 'churn', label: <Tooltip title={ORGANIZATIONS_HELP.filters.churn}><span>Churn risk</span></Tooltip> },
                    { value: 'debt', label: <Tooltip title={ORGANIZATIONS_HELP.filters.debt}><span>In debt</span></Tooltip> },
                  ]}
                />
                </Flex>
                <Table<OrgRow>
                  size="small"
                  rowKey="organizationId"
                  dataSource={organizations}
                  pagination={{ pageSize: 50, hideOnSinglePage: true }}
                  showSorterTooltip={{ target: 'sorter-icon' }}
                  onRow={(row) => ({ onClick: () => setBillingOrganizationId(row.organizationId), style: { cursor: 'pointer' } })}
                  columns={[
                    { title: 'Organization', dataIndex: 'name', sorter: (a, b) => a.name.localeCompare(b.name) },
                    {
                      title: <HelpLabel label="Status" help={ORGANIZATIONS_HELP.status} />,
                      render: (_, row) => billingStatusLabel(row.status),
                    },
                    {
                      title: <HelpLabel label="Price" help={ORGANIZATIONS_HELP.price} />,
                      render: (_, row) => formatCents(row.priceCents),
                    },
                    {
                      title: <HelpLabel label="Balance" help={ORGANIZATIONS_HELP.balance} />,
                      align: 'right',
                      sorter: (a, b) => a.balance - b.balance,
                      render: (_, row) => (
                        <Flex vertical align="end">
                          <Text className="font-mono" type={row.balance <= 0 ? 'danger' : undefined}>
                            {formatSignedCredits(row.balance)}
                          </Text>
                          {row.blockedCode && (
                            <Tooltip title={BLOCKED_CODE_HELP[row.blockedCode]}>
                              <Text type="danger" style={{ fontSize: 12 }}>
                                {blockedCodeLabel(row.blockedCode)}
                              </Text>
                            </Tooltip>
                          )}
                        </Flex>
                      ),
                    },
                    {
                      title: <HelpLabel label="Debt" help={ORGANIZATIONS_HELP.debt} />,
                      align: 'right',
                      sorter: (a, b) => a.debt - b.debt,
                      render: (_, row) => formatCredits(row.debt),
                    },
                    {
                      title: <HelpLabel label="Used" help={ORGANIZATIONS_HELP.used} />,
                      align: 'right',
                      sorter: (a, b) => a.creditsConsumed - b.creditsConsumed,
                      render: (_, row) => formatCredits(Math.ceil(row.creditsConsumed)),
                    },
                    {
                      title: <HelpLabel label="Margin" help={ORGANIZATIONS_HELP.margin} />,
                      align: 'right',
                      render: (_, row) => (row.marginPct === null ? '—' : `${row.marginPct}%`),
                    },
                    {
                      title: <HelpLabel label="Auto-recharge" help={ORGANIZATIONS_HELP.autoRecharge} />,
                      render: (_, row) => (
                        <Tooltip title={AUTO_RECHARGE_STATE_HELP[row.autoRecharge]}>
                          <span>{AUTO_RECHARGE_STATE_LABELS[row.autoRecharge]}</span>
                        </Tooltip>
                      ),
                    },
                    {
                      title: <HelpLabel label="Last spend" help={ORGANIZATIONS_HELP.lastSpend} />,
                      render: (_, row) => formatDate(row.lastSpendAt, '—'),
                    },
                    {
                      title: <HelpLabel label="Churn risk" help={ORGANIZATIONS_HELP.churnRisk} />,
                      render: (_, row) => (
                        <Flex gap={4} wrap="wrap">
                          {row.churnRisk.map((reason) => (
                            <Tooltip key={reason} title={CHURN_RISK_HELP[reason]}>
                              <Tag color="orange">{CHURN_RISK_LABELS[reason]}</Tag>
                            </Tooltip>
                          ))}
                        </Flex>
                      ),
                    },
                  ]}
                />
              </Flex>
            ),
          },
          {
            key: 'events',
            label: `Failed Stripe events${failed.data?.data.items.length ? ` (${failed.data.data.items.length})` : ''}`,
            children: (
              <Flex vertical gap={12}>
                <Text type="secondary">{FAILED_EVENTS_HELP.intro}</Text>
                <Table
                  size="small"
                  rowKey="stripeEventId"
                  pagination={false}
                  loading={failed.isPending}
                  dataSource={failed.data?.data.items ?? []}
                  locale={{ emptyText: 'Every Stripe event went through' }}
                  data-testid="superadmin-failed-events"
                  columns={[
                    {
                      title: <HelpLabel label="Received" help={FAILED_EVENTS_HELP.received} />,
                      render: (_, row) => formatDateTime(row.createdAt),
                    },
                    {
                      title: <HelpLabel label="Event" help={FAILED_EVENTS_HELP.event} />,
                      render: (_, row) => <Text>{row.eventType}</Text>,
                    },
                    {
                      title: <HelpLabel label="Id" help={FAILED_EVENTS_HELP.id} />,
                      render: (_, row) => (
                        <Text type="secondary" copyable>
                          {row.stripeEventId}
                        </Text>
                      ),
                    },
                    {
                      title: <HelpLabel label="Error" help={FAILED_EVENTS_HELP.error} />,
                      render: (_, row) => <Text type="danger">{row.error}</Text>,
                    },
                    {
                      title: '',
                      render: (_, row) => (
                        <Tooltip title={FAILED_EVENTS_HELP.replay}>
                          <Button size="small" loading={replay.isPending} onClick={() => void replayEvent(row.stripeEventId)}>
                            Replay
                          </Button>
                        </Tooltip>
                      ),
                    },
                  ]}
                />
              </Flex>
            ),
          },
          {
            key: 'adjustments',
            label: 'Credit adjustments',
            children: (
              <Flex vertical gap={12}>
                <Text type="secondary">{ADJUSTMENTS_HELP.intro}</Text>
                <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
                  <HelpLabel
                    help={ADJUSTMENTS_HELP.summary}
                    label={
                      <Text data-testid="adjustments-summary">
                        Added <Text strong>{formatCredits(adjustmentTotals.added)}</Text> credits (
                        {formatDollars(addedDollars.retailDollars)} · ≤ {formatDollars(addedDollars.maxCostDollars)} cost) · Taken
                        back <Text strong>{formatCredits(adjustmentTotals.removed)}</Text> credits
                      </Text>
                    }
                  />
                  <Tooltip title={ADJUSTMENTS_HELP.exportCsv}>
                    <Button icon={<Download size="1em" />} onClick={exportAdjustments} disabled={!adjustments.data?.data.items.length}>
                      Export CSV
                    </Button>
                  </Tooltip>
                </Flex>
                <Table
                  size="small"
                  rowKey={(row) => `${row.kind}-${row.grantId}-${row.createdAt}`}
                  loading={adjustments.isPending}
                  dataSource={adjustments.data?.data.items ?? []}
                  pagination={{ pageSize: 50, hideOnSinglePage: true }}
                  locale={{ emptyText: 'No credits added or taken back in this window' }}
                  columns={[
                    { title: 'When', render: (_, row) => formatDateTime(row.createdAt) },
                    { title: 'Organization', dataIndex: 'organizationName' },
                    {
                      title: <HelpLabel label="Credits" help={ADJUSTMENTS_HELP.credits} />,
                      align: 'right',
                      render: (_, row) => (
                        <Text type={row.kind === 'removed' ? 'danger' : 'success'} className="font-mono">
                          {row.kind === 'removed' ? '−' : '+'}
                          {formatCredits(row.credits)}
                        </Text>
                      ),
                    },
                    {
                      title: <HelpLabel label="Value · cost" help={ADJUSTMENTS_HELP.value} />,
                      align: 'right',
                      render: (_, row) =>
                        row.kind === 'removed' ? (
                          <Tooltip title="Value at customer price of the credits taken back. Taking credits back costs K3X nothing.">
                            <Text type="secondary" style={{ fontSize: 12, cursor: 'help' }}>
                              −{formatDollars(creditsInDollars(row.credits, pricing).retailDollars)}
                            </Text>
                          </Tooltip>
                        ) : (
                          <CreditsCost credits={row.credits} pricing={pricing} compact />
                        ),
                    },
                    {
                      title: <HelpLabel label="Reason" help={ADJUSTMENTS_HELP.reason} />,
                      render: (_, row) => supportReasonLabel(row.reasonCode),
                    },
                    {
                      title: <HelpLabel label="Note" help={ADJUSTMENTS_HELP.note} />,
                      render: (_, row) => <Text type="secondary">{row.note}</Text>,
                    },
                    { title: <HelpLabel label="By" help={ADJUSTMENTS_HELP.by} />, render: (_, row) => adjustmentActorLabel(row) },
                  ]}
                />
              </Flex>
            ),
          },
          {
            key: 'plans',
            label: 'Plans',
            children: <SuperadminBillingPlans />,
          },
        ]}
      />

      <SuperadminOrganizationBillingDrawer
        open={billingOrganizationId !== null}
        organizationId={billingOrganizationId}
        onClose={() => setBillingOrganizationId(null)}
      />
    </Flex>
  );
};

export const Route = createFileRoute('/_authenticated/superadmin/billing')({
  component: BillingReportRoute,
});
